<template>
  <div>
    <transition
      name="custom-box-transition"
      enter-active-class="slideInRight"
      leave-active-class="fadeOut"
    >
      <chat-box
        v-if="open"
        :title="chatBoxTitle"
        :showFooter="showAdminChat"
        :activeChat="activeChat"
        @close="closeBox"
        @minimize="minimizeBox"
        @go-back="goBack"
        @send-message="sendMessage"
        style="animation-duration: 0.7s"
      >
        <admin-list
          v-if="showAdminList"
          :admins="conversations"
          @select-admin="openAdminChat"
        />
        <admin-chat
          v-if="showAdminChat"
          :admin="selectedAdmin"
          :messages="adminMessages"
        />
      </chat-box>
    </transition>
    <transition-group
      name="custom-button-transition"
      enter-active-class="slideInUp"
      leave-active-class="fadeOutRight"
    >
      <button
        key="chat-button"
        v-if="showButton"
        class="chat-button"
        @click="openBox"
        style="animation-duration: 0.3s"
      >
        <forum-icon class="chat-button__icon" />
      </button>
      <hcc-badge
        key="chat-badge"
        v-if="showBadge"
        :text="notifications"
        size="sm"
        color="alert"
        fill
        rounded
        customClass="chat-button__badge"
      />
    </transition-group>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import sendInternalMessageGql from '@/graphql/mutations/sendInternalMessage.gql';
import internalChat from '@/graphql/suscriptions/internalChat.gql';
import ForumIcon from '@/assets/icons/forum.svg';
import HccBadge from '@/components/shared/HccBadge/index.vue';
import ChatBox from './ChatBox.vue';
import AdminList from './AdminList.vue';
import AdminChat from './AdminChat.vue';
import { onLogout } from '@/vue-apollo';
import apolloProvider from '@/apollo';

export default {
  components: {
    ForumIcon,
    HccBadge,
    ChatBox,
    AdminList,
    AdminChat,
  },
  data() {
    return {
      showAdminList: false,
      showAdminChat: false,
      open: false,
      selectedAdmin: null,
      activeChat: false,
      messages: [],
      adminMessages: [],
      conversations: [],
      notifications: 0,
    };
  },
  computed: {
    ...mapGetters([
      'userInfo',
    ]),
    activeConversations() {
      return this.$t('chat-button.active-conversations');
    },
    chatBoxTitle() {
      return this.showAdminChat ? this.selectedAdmin.name : this.activeConversations;
    },
    showBadge() {
      return this.notifications !== 0 && !this.open;
    },
    showButton() {
      return this.conversations.length > 0 && !this.open;
    },
  },
  methods: {
    openBox() {
      this.showAdminList = true;
      this.open = true;
    },
    closeBox() {
      this.open = false;
      this.showAdminList = false;
      this.showAdminChat = false;
      this.activeChat = false;
      this.messages = [];
      this.conversations = [];
      this.notifications = 0;
    },
    minimizeBox() {
      this.open = false;
      this.showAdminList = false;
      this.showAdminChat = false;
    },
    openAdminChat(admin) {
      this.showAdminChat = true;
      this.showAdminList = false;
      this.selectedAdmin = admin;
      this.getMessagesFromAdmin();
    },
    goBack() {
      this.showAdminChat = false;
      this.showAdminList = true;
    },
    getMessagesFromAdmin() {
      this.adminMessages = this.selectedAdmin
        ? this.messages.filter((message) => message.id === this.selectedAdmin.id) : [];
    },
    async sendMessage(message) {
      await this.$apollo.mutate({
        mutation: sendInternalMessageGql,
        variables: {
          text: message,
          from: this.userInfo.id,
          to: this.selectedAdmin.id,
        },
      })
        .then(() => {
          this.messages.push({
            id: this.selectedAdmin.id,
            text: message,
            createdAt: new Date().toISOString(),
            received: false,
          });

          this.getMessagesFromAdmin();
        });
    },
  },
  apollo: {
    $subscribe: {
      internalConversation: {
        query: internalChat,
        result({ data }) {
          const { internalConversation: chat } = data;

          if (!this.conversations.some((conversation) => conversation.id === chat.from.id)) {
            this.conversations.push({
              id: chat.from.id,
              email: chat.from.email,
              name: chat.from.name,
              message: chat.text,
            });

            this.notifications += 1;
          }

          this.messages.push({
            id: chat.from.id,
            text: chat.text,
            createdAt: chat.createdAt,
            received: true,
          });

          this.activeChat = true;

          this.getMessagesFromAdmin();
        },
        error(err) {
          if (err.message === 'Missing auth token!') {
            onLogout(apolloProvider.defaultClient);
          }
          this.$toasted.global.error(this.$t('alerts.unexpectedError'));
        },
      },
    },
  },
};
</script>

<style scoped lang="scss">
@import "~styles/components/chatButton/_chat-button.scss";
</style>
